/**
 * Care-note period export — allowlist, grain, projection, and CSV builder.
 *
 * Not a catalogue report. SQL lives in
 * supabase/migrations/20271013000000_care_note_period_export_controlled.sql.
 * This module is the shipped TypeScript engine: period matching, absence
 * matching, field projection, validation, and CSV serialisation.
 */

import { serializeReportToCsv, type ColumnSpec } from '@statura/utils';
import { formatEnum } from '@/lib/format';
import { neutraliseCsvFormula } from '@/lib/csv-formula';
import {
  getCareNoteExportHost,
  isCareNoteExportHostId,
  type CareNoteExportHostId,
} from './care-note-export-hosts';

export const CARE_NOTE_EXPORT_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'COORDINATOR',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'REGISTERED_NURSE',
] as const;

export type CareNoteExportRole = (typeof CARE_NOTE_EXPORT_ROLES)[number];

export function isCareNoteExportRole(role: string | null | undefined): boolean {
  return CARE_NOTE_EXPORT_ROLES.includes(String(role ?? '').toUpperCase() as CareNoteExportRole);
}

export const CARE_NOTE_EXPORT_FIELD_ALLOWLIST = [
  'note_at',
  'client_name',
  'author_staff_name',
  'note_type',
  'source',
  'content',
  'has_note',
  'visit_start',
  'visit_end',
  'service_type',
  'title',
  'tags',
  'linked_goals',
  'ai_generated',
  'row_kind',
] as const;

export type CareNoteExportField = (typeof CARE_NOTE_EXPORT_FIELD_ALLOWLIST)[number];

export const CARE_NOTE_EXPORT_EXCLUDED_FIELDS = [
  'attachments',
  'metadata',
  'transcription_text',
] as const;

export const CARE_NOTE_EXPORT_DEFAULT_FIELDS: CareNoteExportField[] = [
  'note_at',
  'client_name',
  'author_staff_name',
  'note_type',
  'source',
  'content',
];

export const CARE_NOTE_EXPORT_MAX_SPAN_DAYS = 92;
export const CARE_NOTE_EXPORT_MAX_CLIENTS = 500;
export const CARE_NOTE_EXPORT_MAX_ROWS = 10_000;
export const CARE_NOTE_EXPORT_ROW_FETCH_LIMIT = 10_001;

export const CARE_NOTE_EXPORT_DEFAULT_PROVENANCE_EPOCH = '2026-08-03';

export const CARE_NOTE_EXPORT_PROVENANCE_CAVEAT =
  'A visit is listed as missing a note only when no note is linked to that visit. Notes written in the office without a visit link still appear in the notes section. Periods before visit-linking was in place will over-report missing notes.';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const FIELD_COLUMNS: Record<CareNoteExportField, ColumnSpec> = {
  note_at: { key: 'note_at', label: 'Note recorded (local)', type: 'datetime' },
  client_name: { key: 'client_name', label: 'Client', type: 'string', isPhi: true },
  author_staff_name: {
    key: 'author_staff_name',
    label: 'Author / worker',
    type: 'string',
    isPhi: true,
  },
  note_type: { key: 'note_type', label: 'Note type', type: 'string' },
  source: { key: 'source', label: 'Source', type: 'string' },
  content: { key: 'content', label: 'Note content', type: 'string', isPhi: true },
  has_note: { key: 'has_note', label: 'Has care note', type: 'string' },
  visit_start: { key: 'visit_start', label: 'Visit start (local)', type: 'datetime' },
  visit_end: { key: 'visit_end', label: 'Visit end (local)', type: 'datetime' },
  service_type: { key: 'service_type', label: 'Service type', type: 'string' },
  title: { key: 'title', label: 'Title', type: 'string', isPhi: true },
  tags: { key: 'tags', label: 'Tags', type: 'string' },
  linked_goals: { key: 'linked_goals', label: 'Linked goals', type: 'string', isPhi: true },
  ai_generated: { key: 'ai_generated', label: 'AI assisted', type: 'string' },
  row_kind: { key: 'row_kind', label: 'Row kind', type: 'string' },
};

const ROW_KIND_LABELS: Record<string, string> = {
  NOTE: 'Note',
  VISIT_WITHOUT_NOTE: 'Visit without note',
};

export type CareNoteExportNoteSource = {
  id: string;
  clientId: string;
  clientName: string;
  authorStaffName: string;
  noteType: string;
  source: string;
  title: string;
  content: string;
  tags: string;
  aiGenerated: boolean;
  createdLocalDate: string;
  createdLocalAt: string;
  rosterShiftId: string | null;
  serviceDeliveredId: string | null;
  visitStartLocalDate: string | null;
  visitStartLocalAt: string;
  visitEndLocalAt: string;
  serviceType: string;
  linkedGoals: string;
};

export type CareNoteExportVisitSource = {
  rosterShiftId: string;
  clientId: string | null;
  clientName: string;
  workerName: string;
  status: string;
  startLocalDate: string;
  startLocalAt: string;
  endLocalAt: string;
  serviceType: string;
};

export type CareNoteExportDeliveryLink = {
  serviceDeliveredId: string;
  rosterShiftId: string;
};

export type CareNoteExportRawRow = {
  row_kind: 'NOTE' | 'VISIT_WITHOUT_NOTE';
  has_note: boolean;
  note_id: string | null;
  note_at: string;
  client_name: string;
  author_staff_name: string;
  note_type: string;
  source: string;
  title: string;
  content: string;
  tags: string;
  ai_generated: boolean;
  visit_start: string;
  visit_end: string;
  service_type: string;
  linked_goals: string;
};

export type CareNoteExportParams = {
  host: string;
  startDate: string;
  endDate: string;
  clientIds: string[];
  includeUnnotedVisits: boolean;
  fields: string[];
};

export function isIsoDate(value: string): boolean {
  return ISO_DATE.test(value);
}

export function inclusiveDateSpanDays(startDate: string, endDate: string): number {
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  return Math.round((end - start) / 86_400_000);
}

export function dateInInclusiveRange(
  localDate: string | null | undefined,
  startDate: string,
  endDate: string,
): boolean {
  if (!localDate) return false;
  return localDate >= startDate && localDate <= endDate;
}

export function noteIsInPeriod(
  note: CareNoteExportNoteSource,
  startDate: string,
  endDate: string,
): boolean {
  return (
    dateInInclusiveRange(note.createdLocalDate, startDate, endDate) ||
    dateInInclusiveRange(note.visitStartLocalDate, startDate, endDate)
  );
}

export function visitHasLinkedNote(
  visit: CareNoteExportVisitSource,
  notes: readonly CareNoteExportNoteSource[],
  deliveryLinks: readonly CareNoteExportDeliveryLink[],
): boolean {
  const deliveredForVisit = new Set(
    deliveryLinks
      .filter((link) => link.rosterShiftId === visit.rosterShiftId)
      .map((link) => link.serviceDeliveredId),
  );
  return notes.some(
    (note) =>
      note.rosterShiftId === visit.rosterShiftId ||
      (note.serviceDeliveredId !== null && deliveredForVisit.has(note.serviceDeliveredId)),
  );
}

export function visitIsUnnotedAbsence(
  visit: CareNoteExportVisitSource,
  notes: readonly CareNoteExportNoteSource[],
  deliveryLinks: readonly CareNoteExportDeliveryLink[],
): boolean {
  if (visit.status !== 'COMPLETED') return false;
  if (!visit.clientId) return false;
  return !visitHasLinkedNote(visit, notes, deliveryLinks);
}

/** Executable grain spec for the SQL in 20271013000000. Live reads run in the RPC. */
export function selectCareNoteExportRows(input: {
  notes: readonly CareNoteExportNoteSource[];
  visits: readonly CareNoteExportVisitSource[];
  deliveryLinks: readonly CareNoteExportDeliveryLink[];
  startDate: string;
  endDate: string;
  includeUnnotedVisits: boolean;
}): CareNoteExportRawRow[] {
  const rows: CareNoteExportRawRow[] = [];

  for (const note of input.notes) {
    if (!noteIsInPeriod(note, input.startDate, input.endDate)) continue;
    rows.push({
      row_kind: 'NOTE',
      has_note: true,
      note_id: note.id,
      note_at: note.createdLocalAt,
      client_name: note.clientName,
      author_staff_name: note.authorStaffName,
      note_type: note.noteType,
      source: note.source,
      title: note.title,
      content: note.content,
      tags: note.tags,
      ai_generated: note.aiGenerated,
      visit_start: note.visitStartLocalAt,
      visit_end: note.visitEndLocalAt,
      service_type: note.serviceType,
      linked_goals: note.linkedGoals,
    });
  }

  if (input.includeUnnotedVisits) {
    for (const visit of input.visits) {
      if (!dateInInclusiveRange(visit.startLocalDate, input.startDate, input.endDate)) continue;
      if (!visitIsUnnotedAbsence(visit, input.notes, input.deliveryLinks)) continue;
      rows.push({
        row_kind: 'VISIT_WITHOUT_NOTE',
        has_note: false,
        note_id: null,
        note_at: '',
        client_name: visit.clientName,
        author_staff_name: visit.workerName,
        note_type: '',
        source: '',
        title: '',
        content: '',
        tags: '',
        ai_generated: false,
        visit_start: visit.startLocalAt,
        visit_end: visit.endLocalAt,
        service_type: visit.serviceType,
        linked_goals: '',
      });
    }
  }

  return rows;
}

export function isCareNoteExportField(value: string): value is CareNoteExportField {
  return (CARE_NOTE_EXPORT_FIELD_ALLOWLIST as readonly string[]).includes(value);
}

export function resolveExportFields(
  fields: readonly string[],
  includeUnnotedVisits: boolean,
): CareNoteExportField[] {
  const resolved: CareNoteExportField[] = [];
  for (const field of fields) {
    if (!isCareNoteExportField(field)) continue;
    if (!resolved.includes(field)) resolved.push(field);
  }
  if (includeUnnotedVisits && !resolved.includes('has_note')) {
    resolved.unshift('has_note');
  }
  return resolved;
}

export function assertHostAllowsUnnotedVisits(
  offersUnnotedVisits: boolean,
  includeUnnotedVisits: boolean,
): void {
  if (includeUnnotedVisits && !offersUnnotedVisits) {
    throw new Error('care_note_export_unnoted_not_offered');
  }
}

export function validateCareNoteExportRequest(input: CareNoteExportParams): {
  host: CareNoteExportHostId;
  startDate: string;
  endDate: string;
  clientIds: string[];
  includeUnnotedVisits: boolean;
  fields: CareNoteExportField[];
} {
  if (!isCareNoteExportHostId(input.host)) {
    throw new Error('care_note_export_unknown_host');
  }
  const host = getCareNoteExportHost(input.host);
  if (!host) throw new Error('care_note_export_unknown_host');

  if (!isIsoDate(input.startDate) || !isIsoDate(input.endDate)) {
    throw new Error('care_note_export_invalid_dates');
  }
  if (input.startDate > input.endDate) {
    throw new Error('care_note_export_invalid_dates');
  }
  if (inclusiveDateSpanDays(input.startDate, input.endDate) > CARE_NOTE_EXPORT_MAX_SPAN_DAYS) {
    throw new Error('care_note_export_span');
  }

  assertHostAllowsUnnotedVisits(host.offersUnnotedVisits, input.includeUnnotedVisits);

  const fields = resolveExportFields(input.fields, input.includeUnnotedVisits);
  if (fields.length === 0) {
    throw new Error('care_note_export_fields_required');
  }

  const clientIds = input.clientIds.filter((id) => typeof id === 'string' && id.length > 0);
  if (host.clientScope === 'fixed' && clientIds.length === 0) {
    throw new Error('care_note_export_no_visible_clients');
  }
  if (clientIds.length > CARE_NOTE_EXPORT_MAX_CLIENTS) {
    throw new Error('care_note_export_client_limit');
  }

  return {
    host: input.host,
    startDate: input.startDate,
    endDate: input.endDate,
    clientIds,
    includeUnnotedVisits: input.includeUnnotedVisits && host.offersUnnotedVisits,
    fields,
  };
}

export function assertExportRowBudget(rowCount: number): void {
  if (rowCount > CARE_NOTE_EXPORT_MAX_ROWS) {
    throw new Error('care_note_export_row_limit');
  }
}

export const CARE_NOTE_EXPORT_ACCESS_REFUSED = 'care_note_export_access_refused';
export const CARE_NOTE_EXPORT_ACK_REQUIRED = 'care_note_export_ack_required';

export function resolveCareNoteExportProvenanceEpoch(
  cutoverDate: string | null | undefined,
): string {
  if (typeof cutoverDate === 'string' && ISO_DATE.test(cutoverDate)) {
    return cutoverDate;
  }
  return CARE_NOTE_EXPORT_DEFAULT_PROVENANCE_EPOCH;
}

export function shouldWarnProvenance(
  startDate: string,
  includeUnnotedVisits: boolean,
  epoch: string = CARE_NOTE_EXPORT_DEFAULT_PROVENANCE_EPOCH,
): boolean {
  return includeUnnotedVisits && startDate < epoch;
}

export function evaluateProvenanceAcknowledgement(input: {
  startDate: string;
  includeUnnotedVisits: boolean;
  epoch: string;
  acknowledged: boolean;
}): { warning: boolean; blocked: boolean } {
  const warning = shouldWarnProvenance(input.startDate, input.includeUnnotedVisits, input.epoch);
  return { warning, blocked: warning && !input.acknowledged };
}

function displayEnum(value: string): string {
  if (!value) return '';
  return formatEnum(value);
}

export function neutralizeCsvFormula(value: string): string {
  if (!value) return value;
  // A plain numeric literal stays numeric; everything else goes through the ONE shared rule
  // (lib/csv-formula.ts; release-owner delta-3).
  if (/^-?\d+(?:\.\d+)?(?:e[+-]?\d+)?$/i.test(value.trim())) return value;
  return String(neutraliseCsvFormula(value) ?? '');
}

export function formatCareNoteExportValue(
  field: CareNoteExportField,
  row: CareNoteExportRawRow,
): string {
  switch (field) {
    case 'has_note':
      return row.has_note ? 'Yes' : 'No';
    case 'row_kind':
      return ROW_KIND_LABELS[row.row_kind] ?? displayEnum(row.row_kind);
    case 'note_type':
    case 'source':
    case 'service_type':
      return displayEnum(row[field]);
    case 'ai_generated':
      return row.ai_generated ? 'Yes' : 'No';
    case 'note_at':
    case 'client_name':
    case 'author_staff_name':
    case 'content':
    case 'visit_start':
    case 'visit_end':
    case 'title':
    case 'tags':
    case 'linked_goals':
      return neutralizeCsvFormula(row[field] ?? '');
    default: {
      const _never: never = field;
      return String(_never);
    }
  }
}

export function projectCareNoteExportRow(
  row: CareNoteExportRawRow,
  fields: readonly CareNoteExportField[],
): Record<string, string> {
  const projected: Record<string, string> = {};
  for (const field of fields) {
    projected[field] = formatCareNoteExportValue(field, row);
  }
  return projected;
}

export function defaultCareNoteExportRange(
  days: number,
  todayIso: string,
): { startDate: string; endDate: string } {
  const [year, month, day] = todayIso.split('-').map(Number);
  const start = new Date(Date.UTC(year!, month! - 1, day! - days));
  const startDate = `${String(start.getUTCFullYear()).padStart(4, '0')}-${String(start.getUTCMonth() + 1).padStart(2, '0')}-${String(start.getUTCDate()).padStart(2, '0')}`;
  return { startDate, endDate: todayIso };
}

export function careNoteExportFilename(startDate: string, endDate: string): string {
  const start = startDate.replace(/-/g, '');
  const end = endDate.replace(/-/g, '');
  return `care-notes-${start}-${end}.csv`;
}

export function prependCsvPreamble(csv: string, warning: boolean): string {
  if (!warning) return csv;
  return `# ${CARE_NOTE_EXPORT_PROVENANCE_CAVEAT}\r\n${csv}`;
}

export function buildCareNoteExportAuditChanges(input: {
  host: string;
  startDate: string;
  endDate: string;
  fields: readonly string[];
  noteCount: number;
  absenceCount: number;
  clientIds: readonly string[];
  role: string;
  includeUnnotedVisits: boolean;
}): Record<string, unknown> {
  return {
    exportType: 'care_note_period',
    host: input.host,
    startDate: input.startDate,
    endDate: input.endDate,
    fields: [...input.fields],
    noteCount: input.noteCount,
    absenceCount: input.absenceCount,
    clientIds: [...input.clientIds],
    role: input.role,
    includeUnnotedVisits: input.includeUnnotedVisits,
  };
}

export function serializeCareNoteExportCsv(
  rows: readonly CareNoteExportRawRow[],
  fields: readonly CareNoteExportField[],
  provenanceWarning = false,
): string {
  const projected = rows.map((row) => projectCareNoteExportRow(row, fields));
  const columns = fields.map((field) => FIELD_COLUMNS[field]);
  return prependCsvPreamble(serializeReportToCsv(columns, projected), provenanceWarning);
}

export function normalizeRpcExportRow(row: Record<string, unknown>): CareNoteExportRawRow {
  const kind = row.row_kind === 'VISIT_WITHOUT_NOTE' ? 'VISIT_WITHOUT_NOTE' : 'NOTE';
  return {
    row_kind: kind,
    has_note: Boolean(row.has_note),
    note_id: typeof row.note_id === 'string' ? row.note_id : null,
    note_at: String(row.note_at ?? ''),
    client_name: String(row.client_name ?? ''),
    author_staff_name: String(row.author_staff_name ?? ''),
    note_type: String(row.note_type ?? ''),
    source: String(row.source ?? ''),
    title: String(row.title ?? ''),
    content: String(row.content ?? ''),
    tags: String(row.tags ?? ''),
    ai_generated: Boolean(row.ai_generated),
    visit_start: String(row.visit_start ?? ''),
    visit_end: String(row.visit_end ?? ''),
    service_type: String(row.service_type ?? ''),
    linked_goals: String(row.linked_goals ?? ''),
  };
}

export function buildCareNotePeriodExport(input: {
  notes: readonly CareNoteExportNoteSource[];
  visits?: readonly CareNoteExportVisitSource[];
  deliveryLinks?: readonly CareNoteExportDeliveryLink[];
  startDate: string;
  endDate: string;
  includeUnnotedVisits: boolean;
  fields: readonly string[];
  provenanceWarning?: boolean;
}): {
  csv: string;
  rows: CareNoteExportRawRow[];
  projected: Record<string, string>[];
  noteCount: number;
  absenceCount: number;
  fields: CareNoteExportField[];
  filename: string;
} {
  const fields = resolveExportFields(input.fields, input.includeUnnotedVisits);
  const rows = selectCareNoteExportRows({
    notes: input.notes,
    visits: input.visits ?? [],
    deliveryLinks: input.deliveryLinks ?? [],
    startDate: input.startDate,
    endDate: input.endDate,
    includeUnnotedVisits: input.includeUnnotedVisits,
  });
  assertExportRowBudget(rows.length);
  const projected = rows.map((row) => projectCareNoteExportRow(row, fields));
  const csv = serializeCareNoteExportCsv(rows, fields, Boolean(input.provenanceWarning));
  const noteCount = rows.filter((row) => row.row_kind === 'NOTE').length;
  const absenceCount = rows.filter((row) => row.row_kind === 'VISIT_WITHOUT_NOTE').length;
  return {
    csv,
    rows,
    projected,
    noteCount,
    absenceCount,
    fields,
    filename: careNoteExportFilename(input.startDate, input.endDate),
  };
}

export function mapCareNoteExportError(error: unknown): string {
  const isPreparationError =
    error instanceof Error && error.name === 'CareNoteExportPreparationError';
  const preparationCode =
    isPreparationError && 'code' in error && typeof error.code === 'string'
      ? error.code
      : undefined;
  if (
    (isPreparationError && preparationCode !== CARE_NOTE_EXPORT_ACK_REQUIRED) ||
    (error instanceof Error && error.message === CARE_NOTE_EXPORT_ACCESS_REFUSED)
  )
    return "You don't have access to export care notes for this area.";
  const message = preparationCode ?? (error instanceof Error ? error.message : String(error ?? ''));
  if (message.includes('care_note_export_unknown_host'))
    return 'This export is not available here.';
  if (message.includes('care_note_export_invalid_dates'))
    return 'Choose a valid start and end date.';
  if (message.includes('care_note_export_span')) {
    return 'Choose a period of 92 days or less.';
  }
  if (message.includes('care_note_export_no_visible_clients')) {
    return 'No participants you can open fall in this export.';
  }
  if (message.includes('care_note_export_client_limit')) {
    return 'This export is limited to 500 participants.';
  }
  if (message.includes('care_note_export_row_limit')) {
    return 'Too many rows for one file. Shorten the period or select fewer participants.';
  }
  if (message.includes('care_note_export_fields_required')) {
    return 'Choose at least one field.';
  }
  if (message.includes('care_note_export_unnoted_not_offered')) {
    return 'Visits without a note cannot be included from this page.';
  }
  if (message.includes(CARE_NOTE_EXPORT_ACK_REQUIRED)) {
    return 'Acknowledge the missing-note caveat before exporting.';
  }
  if (message.includes('role_not_authorised'))
    return 'You do not have permission to export care notes.';
  if (message.includes('care_note_export_pdf_font_unsupported'))
    return 'This PDF cannot faithfully render some selected text. Download CSV to preserve it and contact support.';
  return 'Could not export care notes.';
}
